import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Geometry } from 'geojson';
import { parseError } from 'hooks/shared/error';
import { ContactMethod } from 'components/Notifications/recipientTypes';
import apiConfig from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid, selectIncidentGuid } from 'slices/commonSelectors';
import type { NotificationDeliveryReportDto } from 'components/MapPage/CorporateViewTabs/NotificationsTab/notificationDeliveryModel';
import type { ResponseOption } from 'components/MapPage/CorporateViewTabs/NotificationsTab/responseOptions';
import type { EscalationPlan } from 'components/MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';
import type { NotificationSendDto } from 'components/MapPage/CorporateViewTabs/NotificationsTab/notificationSendsModel';

export const filterOptions = ['active', 'historical', 'sent'];

export type NotificationDto = {
  id: string;
  groupGuid: string;
  incidentId: string | null;
  acknowledgementRequired: boolean;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
  sentAt: string | null;
  autoArchiveDate: string | null;
  priority: NotificationPriority;
  status: NotificationStatus;
  sourceItemId: string | null;
  scheduledDate: string | null;
  aors: string[];
  escalationPlan?: EscalationPlan | null;
};

export type NotificationMetadataDto = {
  id: string;
  notificationId: string;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  phoneMessage: string | null;
  appMessage: string | null;
  createdAt: string;
  updatedAt: string;
  users: string[];
  teams: string[];
  sendEmail: boolean;
  sendSms: boolean;
  sendTeams: boolean;
  sendPhone: boolean;
  sendApp: boolean;
};

export type NotificationWithMetadataDto = NotificationDto & {
  metadata: NotificationMetadataDto;
  hasUnarchivedRecipients?: boolean;
};

export enum NotificationStatus {
  CREATED = 'created', // status for tracking. notification has been created
  DRAFT = 'draft', // user saved notification as a draft
  SCHEDULED = 'scheduled', // notification is waiting in BullMQ
  PROCESSING = 'processing', // status is for tracking. notification is being processed by worker
  CANCELLED = 'cancelled', // user cancelled before sending
  SENT = 'sent', // successfully sent to all recipients
  FAILED = 'failed', // failed to send to any recipient
  PARTIAL = 'partial', // Some recipients succeeded, some failed
}

export enum NotificationType {
  EMAIL = 'email',
  SMS = 'sms',
  IN_APP = 'inApp',
  TEAMS = 'teams',
  TTS_HOME = 'ttsHome',
  TTS_CELL = 'ttsCell',
}

export enum NotificationPriority {
  INFO = 'info',
  ADVISORY = 'advisory',
  WATCH = 'watch',
  WARNING = 'warning',
  CRITICAL = 'critical',
  STAND_DOWN_SAFETY = 'stand_down_safety',
}

// Currently in use by the legacy notification alert system
export type SendNotificationFormFields = {
  subject: string;
  contact_method: ContactMethod[];
  unified_message_choice: boolean;
  email_message?: string;
  sms_message?: string;
  unified_message?: string;
  individuals?: UUID[];
  teams?: UUID[];
  sendType: string;
  scheduledDate?: Date | null;
};

export type CreateNotificationFormFields = {
  incidentId: string | null;
  acknowledgementRequired: boolean;
  autoArchiveDate: Date | null;
  priority: NotificationPriority;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  phoneMessage: string | null;
  appMessage: string | null;
  userIds: string[];
  teamIds: string[];
  sourceItemId: string | null;
  sourceItemType: string | null;
  scheduledDate: Date | null;
  notificationTypes: NotificationType[];
  aors?: string[];
  /**
   * Only sent when acknowledgementRequired is on. Null leaves the server on its
   * single confirm-receipt default.
   */
  responseOptions?: ResponseOption[] | null;
  /**
   * The follow-up ladder for recipients who stay silent. Also only sent
   * alongside acknowledgementRequired, since the server rejects a cascade with
   * no way for a recipient to stop it.
   */
  escalationPlan?: EscalationPlan | null;
  /**
   * Channel bodies sent after the recipient acknowledges. Null leaves the first
   * message as the only one.
   */
  followUpMessage?: {
    emailSubject?: string | null;
    emailMessage?: string | null;
    smsMessage?: string | null;
    teamsMessage?: string | null;
    phoneMessage?: string | null;
    appMessage?: string | null;
  } | null;
  /**
   * Set when Send to everyone stores a filter for delivery to resolve. A
   * hand-picked subset leaves this empty and sends user ids only.
   */
  audienceGeometry?: Geometry | null;
  audienceAorItemIds?: string[];
  audienceLocationMatch?: 'home_aor' | 'current_location' | null;
  /** File Manager file/folder ids from the workspace or organization library. */
  fileIds?: string[];
};

export type AudiencePreviewDto = {
  userCount: number;
  userGuids?: string[];
  aorNames: string[];
  unreachableByChannel: Record<string, number>;
  /** Per channel, who cannot be reached on it, by display name. */
  unreachableNamesByChannel?: Record<string, string[]>;
  empty: boolean;
};

/**
 * How many people a geographic audience resolves to, before sending.
 *
 * Coverage data is sparse, so a perfectly valid polygon can resolve to nobody.
 * The composer asks this on every edit of the shape so that emptiness surfaces
 * while the operator is still drawing rather than after they hit send.
 */
export const useAudiencePreview = (values: {
  geometry?: Geometry | null;
  aorItemIds?: string[];
  /** Named recipients, for checking whether they can be reached at all. */
  userIds?: string[];
  teamIds?: string[];
  roleScopeKeys?: string[];
  roleIds?: string[];
  notificationTypes?: NotificationType[];
  enabled?: boolean;
}) => {
  const groupGuid = useSelector(selectGroupGuid);
  const hasAudience =
    Boolean(values.geometry) ||
    Boolean(values.aorItemIds?.length) ||
    Boolean(values.userIds?.length) ||
    Boolean(values.teamIds?.length) ||
    Boolean(values.roleScopeKeys?.length) ||
    Boolean(values.roleIds?.length);

  return useQuery<AudiencePreviewDto>({
    queryKey: [
      'audiencePreview',
      groupGuid,
      values.geometry,
      values.aorItemIds,
      values.userIds,
      values.teamIds,
      values.roleScopeKeys,
      values.roleIds,
      values.notificationTypes,
    ],
    enabled: Boolean(groupGuid) && hasAudience && values.enabled !== false,
    queryFn: async () => {
      const response = await axios.post(
        `${apiConfig.apiGateway.notifications}/audience/preview`,
        {
          groupGuid,
          geometry: values.geometry ?? undefined,
          aorItemIds: values.aorItemIds,
          userIds: values.userIds,
          teamIds: values.teamIds,
          roleScopeKeys: values.roleScopeKeys,
          roleIds: values.roleIds,
          notificationTypes: values.notificationTypes,
        }
      );
      return response.data;
    },
  });
};

export const useCreateNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateNotificationFormFields) => {
      await axios(apiConfig.apiGateway.notifications, {
        method: 'POST',
        data: JSON.stringify({
          ...data,
          groupGuid: groupGuid,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      if (groupGuid) {
        queryClient.invalidateQueries({
          queryKey: ['notificationsByGroup', groupGuid],
        });
      }
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useSendNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const incidentGuid = useSelector(selectIncidentGuid);
  return useMutation({
    mutationFn: async (data: SendNotificationFormFields) => {
      return await axios(apiConfig.apiGateway.sendPratusAlert, {
        method: 'POST',
        data: JSON.stringify({
          ...data,
          group_guid: groupGuid,
          incident_guid: incidentGuid,
        }),
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useNotificationsByGroup = () => {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<NotificationWithMetadataDto[]>({
    queryKey: ['notificationsByGroup', groupGuid],
    enabled: !!groupGuid,
    queryFn: async () => {
      const response = await axios.get(apiConfig.apiGateway.notifications, {
        params: { groupId: groupGuid },
      });
      return response.data;
    },
  });
};

/**
 * Per-recipient delivery report for one sent notification.
 *
 * The SSE stream now pushes both recipient replies and provider receipts, so
 * the poll is a backstop for a dropped stream rather than the mechanism. Kept
 * slow for that reason: at 15s it was re-fetching the whole report four times a
 * minute to discover changes it was already being told about.
 */
export const useNotificationDeliveries = (
  notificationId: string | null,
  options?: { enabled?: boolean }
) => {
  return useQuery<NotificationDeliveryReportDto>({
    queryKey: ['notificationDeliveries', notificationId],
    enabled: Boolean(notificationId) && options?.enabled !== false,
    refetchInterval: 120_000,
    queryFn: async () => {
      const response = await axios.get(
        `${apiConfig.apiGateway.notifications}/${notificationId}/deliveries`
      );
      return response.data;
    },
  });
};

/** Re-sends to the recipients who have neither acknowledged nor replied. */
export const useRenotifyUnresponded = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      notificationId: string;
      recipientUserGuid?: string;
    }) => {
      const response = await axios.post<{
        notificationId: string | null;
        recipientCount: number;
      }>(
        `${apiConfig.apiGateway.notifications}/${data.notificationId}/renotify`,
        data.recipientUserGuid
          ? { recipientUserGuid: data.recipientUserGuid }
          : {}
      );
      return response.data;
    },
    onSuccess: (result, variables) => {
      if (result.recipientCount === 0) {
        toast.info('Everyone has already responded. Nothing was sent.');
      } else {
        toast.success(
          `Re-sent to ${result.recipientCount} recipient${result.recipientCount === 1 ? '' : 's'}.`
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['notificationDeliveries', variables.notificationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

/**
 * The organisation's send history for the sends dashboard.
 *
 * Distinct from `useNotificationsByGroup`, which the server filters to the
 * caller's own sends. This one needs the org-level permission and returns what
 * everybody sent.
 */
export const useOrgNotificationSends = (options?: { enabled?: boolean }) => {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<NotificationSendDto[]>({
    queryKey: ['notificationSends', groupGuid],
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get(
        `${apiConfig.apiGateway.notifications}/sends`,
        { params: { groupId: groupGuid } }
      );
      return response.data;
    },
  });
};

/** One send in full, for the dashboard's detail page. */
export const useOrgNotificationSend = (notificationId: string | undefined) => {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<{
    notification: NotificationWithMetadataDto;
    report: NotificationDeliveryReportDto;
  }>({
    queryKey: ['notificationSend', notificationId, groupGuid],
    enabled: Boolean(notificationId) && Boolean(groupGuid),
    queryFn: async () => {
      const response = await axios.get(
        `${apiConfig.apiGateway.notifications}/sends/${notificationId}`,
        { params: { groupId: groupGuid } }
      );
      return response.data;
    },
  });
};

/** Calls off a send that has not gone out yet, along with any cascade behind it. */
export const useCancelNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      return await axios.post(
        `${apiConfig.apiGateway.notifications}/${data.notificationId}/cancel`
      );
    },
    onSuccess: () => {
      toast.success('Notification cancelled.');
      queryClient.invalidateQueries({
        queryKey: ['notificationSends', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useArchiveNotificationForAll = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      return await axios.post(
        `${apiConfig.apiGateway.notifications}/${data.notificationId}/archive-all`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
